/**
 * Client for /api/item-review. Thin on purpose: the desk holds no business rules that
 * the backend does not also enforce, because the backend is the one that has to be
 * right. Validation here exists to give the reviewer an answer instantly, never to be
 * the only check.
 */
import { api } from '../config/api';
import {
  ItemDetail, ItemEditPayload, QueueItem, ReviewActionName, ReviewStats, ReviewerContext,
} from './reviewTypes';

export async function myExams(): Promise<string[]> {
  const { data } = await api.get<string[]>('/api/item-review/exams');
  return data;
}

export async function getContext(exam: string): Promise<ReviewerContext> {
  const { data } = await api.get<ReviewerContext>(`/api/item-review/${exam}/context`);
  return data;
}

export async function getQueue(
  exam: string,
  opts: { status?: string; flaggedOnly?: boolean; search?: string; limit?: number } = {},
): Promise<QueueItem[]> {
  const { data } = await api.get<QueueItem[]>(`/api/item-review/${exam}/queue`, {
    params: {
      status: opts.status ?? 'ai_unreviewed',
      flagged_only: opts.flaggedOnly ?? false,
      search: opts.search || undefined,
      limit: opts.limit ?? 100,
    },
  });
  return data;
}

export async function getItem(exam: string, itemId: string): Promise<ItemDetail> {
  const { data } = await api.get<ItemDetail>(`/api/item-review/${exam}/items/${itemId}`);
  return data;
}

export async function act(
  exam: string,
  itemId: string,
  body: { action: ReviewActionName; reason_code?: string; notes?: string; seconds_spent?: number },
): Promise<ItemDetail> {
  const { data } = await api.post<ItemDetail>(`/api/item-review/${exam}/items/${itemId}/action`, body);
  return data;
}

export async function editItem(exam: string, itemId: string, body: ItemEditPayload): Promise<ItemDetail> {
  const { data } = await api.patch<ItemDetail>(`/api/item-review/${exam}/items/${itemId}`, body);
  return data;
}

export async function getStats(exam: string): Promise<ReviewStats> {
  const { data } = await api.get<ReviewStats>(`/api/item-review/${exam}/stats`);
  return data;
}

// Pure rules live in reviewRules.ts (no network import, so they are unit-testable).
export { validateOptions, rejectionProblems, stemOf } from './reviewRules';
